import { ChangeDetectionStrategy, ChangeDetectorRef, Component, input, output, signal, effect, DestroyRef } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { JsonDeliveryHole } from 'src/app/bo/delivery';
import { DeliveryParams, HoleCondition, Detonator } from 'src/app/services/hole.service';

@Component({
  selector: 'app-modify-hole',
  templateUrl: './modify-hole.component.html',
  styleUrls: ['./modify-hole.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ModifyHoleComponent {

  selectedHoleIndex = signal<number>(-1);
  isDetailsVisible = signal<boolean[]>([]);
  form = signal<FormGroup | undefined>(undefined);
  detosLibelle = signal<string>('');
  selectedConditions = signal<HoleCondition[]>([]);
  detonators = signal<Detonator[]>([]);
  destDeliveryName = signal<string>('');
  initialValues = signal<any[]>([]);

  totalHoles = signal<number>(0);
  isLoading = signal<boolean>(false);
  isInitializing = signal<boolean>(true);

  holesDeliveryControl = input.required<FormArray>();
  deliveryParams = input.required<DeliveryParams>();
  totalHolesCount = input<number>(0);
  currentLoadedCount = input<number>(0);
  isScrollLoading = input<boolean>(false);
  
  updateDelivery = output<boolean>();
  splitDelivery = output<string>();
  loadMoreHoles = output<void>();

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {
    effect(() => {
      const params = this.deliveryParams();
      if (params) {
        this.detonators.set(params.detonators.map((deto) => ({
          commercialName: deto.commercialName,
          productId: deto.product.id,
          product: deto.product,
          id: deto.id,
        })));
      }
    });

    effect(() => {
      const total = this.totalHolesCount();
      this.totalHoles.set(total);
    });
  }

  ngOnInit(): void {
    const tempVisible: boolean[] = [];
    const tempInitial: any[] = [];
    
    this.holesDeliveryControl().controls.forEach((holeControl, index) => {
      tempVisible.push(false);
      tempInitial.push(JSON.parse(JSON.stringify(holeControl.value)));
      
      this.ensureMinimumDetonators(holeControl as FormGroup);
    });

    this.isDetailsVisible.set(tempVisible);
    this.initialValues.set(tempInitial);

    this.isInitializing.set(false);
    this.changeDetectorRef.detectChanges();
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 200;
    
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {
      if (!this.isScrollLoading() && this.currentLoadedCount() < this.totalHolesCount()) {
        this.loadMoreHoles.emit();
      }
    }
  }

  private ensureMinimumDetonators(holeFormGroup: FormGroup): void {
    const detosControl = holeFormGroup.get('detos') as FormArray;
    if (detosControl && detosControl.length === 0) {
      while (detosControl.length < 2) {
        detosControl.push(new FormGroup({
          productId: new FormControl(null),
          count: new FormControl(null),
        }));
      }
    }
  }

  getDetosCount(form: FormGroup): number {
    let detos = form.get('detos')?.value;
    let detosCount = 0;
    if (detos) {
      for (let i = 0; i < detos.length; i++) {
        const deto = detos[i];
        detosCount += deto.count;
      }
    }
    return detosCount;
  }

  toggleDetails(index: number): void {
    this.isDetailsVisible.update(arr => {
      const newArr = [...arr];
      newArr[index] = !newArr[index];
      return newArr;
    });
    this.selectedHoleIndex.set(index);
  }

  truncateText(text: string): string {
    if (text.length > 20) {
      return text.substring(0, 20) + '...';
    }
    return text;
  }

  getHoleConditionLabel(condition: HoleCondition[]): string {
    return condition.map(c => c.shortName).join('- ');
  }

  clearConditions() {
    this.selectedConditions.set([]);
  }

  onSubmit(index: number) {
    this.updateDelivery.emit(true);
    this.toggleDetails(index)
  }

  onSplitDelivery(destDeliveryName: string) {
    this.splitDelivery.emit(destDeliveryName);
  }

  protected resetForGroup(formGroup: FormGroup): void {
    formGroup.reset();
  }

  onCancel(formGroup: FormGroup, index: number) {
    const original = this.initialValues()[index];
    if (!original) return;
    formGroup.patchValue(original);
    const detosControl = formGroup.get('detos') as FormArray;
    if (detosControl) {
      detosControl.clear();

      for (const deto of original.detos) {
        detosControl.push(new FormGroup({
          productId: new FormControl(deto.productId),
          count: new FormControl(deto.count),
        }));
      }
    }
    this.toggleDetails(index);
  }
  preventScientificInput(event: KeyboardEvent): void {
    if (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-') {
      event.preventDefault();
    }
  }
}
